import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { ArrowUpRight } from "lucide-react";
import LanguageSwitcher from "../Botones/Botones";
import logo from "../Home/Images/ELTALLERLOGO.png";

const HeaderShell = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1000;
  height: 72px;
  padding: 0 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.24);
  background: rgba(246, 239, 226, 0.3);
  backdrop-filter: blur(15px) saturate(1.2);
  -webkit-backdrop-filter: blur(15px) saturate(1.2);

  @media (min-width: 960px) {
    height: 82px;
    padding: 0 3.5vw;
    border-bottom-color: rgba(23, 63, 53, 0.14);
    background: rgba(250, 246, 237, 0.9);
  }
`;

const Nav = styled.nav`
  width: min(100%, 1540px);
  height: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Brand = styled(NavLink)`
  position: relative;
  z-index: 1002;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Logo = styled.img`
  width: 102px;
  height: 68px;
  object-fit: contain;
  @media (min-width: 960px) { width: 62px; height: 62px; }
`;
const BrandWordmark = styled.span`
  display: none;
`;

const DesktopLinks = styled.div`
  display: none;
  align-items: center;
  gap: clamp(0.8rem, 1.35vw, 1.45rem);
  @media (min-width: 960px) { display: flex; }
`;

const Link = styled(NavLink)`
  color: #173f35;
  font-family: Arial, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  &::after {
    content: "";
    display: block;
    width: 0;
    height: 1px;
    margin-top: 3px;
    background: #cf7046;
    transition: width 0.25s ease;
  }
  &:hover::after, &.active::after { width: 100%; }
`;
const DesktopAdminLink = styled(Link)`
  padding: 0.6rem 0.85rem;
  border: 1px solid rgba(23, 63, 53, 0.42);
  border-radius: 999px;
  letter-spacing: 0.1em;
  &::after { display: none; }
  &:hover, &.active { border-color: #173f35; background: #173f35; color: #fff; }
`;

const Tools = styled.div`
  position: relative;
  z-index: 1002;
  display: flex;
  align-items: center;
  gap: 0.7rem;
`;

const MenuButton = styled.button`
  width: 34px;
  height: 42px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: #173f35;
  padding: 0;
  @media (min-width: 960px) { display: none; }

  span {
    position: absolute;
    width: 26px;
    height: 2px;
    background: currentColor;
    transition: transform .3s ease, opacity .2s ease;
  }
  span:nth-child(1) { transform: ${({ $open }) => $open ? "rotate(45deg)" : "translateY(-6px)"}; }
  span:nth-child(2) { opacity: ${({ $open }) => $open ? 0 : 1}; }
  span:nth-child(3) { transform: ${({ $open }) => $open ? "rotate(-45deg)" : "translateY(6px)"}; }

  &:focus-visible {
    outline: none;
  }
`;

const MobilePanel = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999;
  width: 100vw;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: calc(72px + 1.5rem) 1.25rem max(2rem, env(safe-area-inset-bottom));
  background:
    radial-gradient(circle at 88% 10%, rgba(207,112,70,.35), transparent 28%),
    #d9e7a8;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
  transform: ${({ $open }) => ($open ? "translateY(0)" : "translateY(-18px)")};
  transition: opacity .35s ease, transform .35s ease, visibility .35s;
  overflow-y: auto;
`;

const MobileLink = styled(NavLink)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 58px;
  padding: .65rem 0;
  border-bottom: 1px solid rgba(23,63,53,.22);
  color: #173f35;
  font-size: clamp(1.75rem, 8vw, 3rem);
  line-height: 1;
  letter-spacing: -.035em;
  &.active { color: #b45331; }
`;

const MobileArrow = styled(ArrowUpRight)`
  width: .95rem;
  height: .95rem;
  flex-shrink: 0;
`;

const MenuFoot = styled.p`
  margin-top: auto;
  color: #173f35;
  font-family: Arial, sans-serif;
  font-size: .7rem;
  letter-spacing: .12em;
  text-transform: uppercase;
`;

const AdminLink = styled(MobileLink)`
  margin-top: 1.2rem;
  padding: 0.8rem 1rem;
  border: 1px solid rgba(23,63,53,.28);
  border-radius: 999px;
  font-family: Arial, sans-serif;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const Header = () => {
  const { t, i18n } = useTranslation();
  const { pathname, hash } = useLocation();
  const [open, setOpen] = useState(false);
  const links = [
    ["/", t("header.inicio")],
    ["/experiencias-ceramica-granollers", t("header.reserva")],
    ["/eventos", t("header.eventos")],
    ["/quienes-somos", t("header.quienes")],
    ["/regala-pinta-tu-ceramica-granollers", t("header.regala")],
    ["/#preguntas-frecuentes", i18n.language?.startsWith("ca") ? "Preguntes freqüents" : "Preguntas frecuentes"],
    ["/contacto", t("header.contacto")],
  ];

  useEffect(() => setOpen(false), [pathname, hash]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <HeaderShell>
        <Nav aria-label={t("accessibility.mainNavigation")}>
        <Brand to="/" aria-label={t("accessibility.homeLabel")}><Logo src={logo} alt="El Taller" /><BrandWordmark><strong>El Taller</strong><small>Ceramic · Coffee · Art</small></BrandWordmark></Brand>
        <DesktopLinks>
          {links.map(([to, label]) => <Link key={to} to={to}>{label}</Link>)}
          <DesktopAdminLink to="/admin/reservas">Admin</DesktopAdminLink>
        </DesktopLinks>
        <Tools>
          <LanguageSwitcher />
          <MenuButton $open={open} type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-label={open ? t("accessibility.closeMenu") : t("accessibility.openMenu")}><span /><span /><span /></MenuButton>
        </Tools>
        </Nav>
      </HeaderShell>
      <MobilePanel $open={open} aria-hidden={!open}>
        {links.map(([to, label], index) => <MobileLink key={to} to={to} tabIndex={open ? 0 : -1}><span>{String(index + 1).padStart(2, "0")}.</span> {label}<MobileArrow aria-hidden="true" /></MobileLink>)}
        <AdminLink to="/admin/reservas" tabIndex={open ? 0 : -1}><span>Admin</span><MobileArrow aria-hidden="true" /></AdminLink>
        <MenuFoot>Vilanova del Vallès · Barcelona</MenuFoot>
      </MobilePanel>
    </>
  );
};

export default Header;
